import { type FormikTouched } from 'formik';

import { VISIBILITY_EXPRESSION_TYPES } from '../../../shared/constants/global';
import { type Expression, type FormValues } from '../../../shared/types';
import { evaluateExpression } from '../../ExpressionBuilder/utils';
import { type EntityInitialData } from '../data-store';

export interface DaVisibilityEvaluationContext {
  values: FormValues;
  touched: FormikTouched<FormValues>;
  entityData: EntityInitialData | null;
}

/**
 * True when the VISIBILITY expression has at least one non-empty SHOW and one non-empty HIDE value.
 */
export const visibilityHasShowAndHideConfigured = (
  visibilityExpression: Expression
): boolean => {
  const nonEmpty = visibilityExpression.value.filter(
    (entry) => (entry.value?.trim() ?? '') !== ''
  );

  const hasShow = nonEmpty.some(
    (entry) => entry.kind === VISIBILITY_EXPRESSION_TYPES.SHOW
  );
  const hasHide = nonEmpty.some(
    (entry) => entry.kind === VISIBILITY_EXPRESSION_TYPES.HIDE
  );

  return hasShow && hasHide;
};

/**
 * True when every non-empty SHOW/HIDE sub-expression evaluates to false.
 */
export const allVisibilitySubExpressionsFalse = (
  visibilityExpression: Expression,
  context: DaVisibilityEvaluationContext
): boolean => {
  const toEvaluate = visibilityExpression.value.filter(
    (entry) =>
      (entry.kind === VISIBILITY_EXPRESSION_TYPES.SHOW ||
        entry.kind === VISIBILITY_EXPRESSION_TYPES.HIDE) &&
      (entry.value?.trim() ?? '') !== ''
  );

  if (toEvaluate.length === 0) {
    return false;
  }

  return toEvaluate.every(
    (entry) =>
      !evaluateExpression(entry.value ?? '', {
        values: context.values,
        touched: context.touched,
        entityData: context.entityData,
      })
  );
};

/**
 * DA-only: when standard visibility says hidden but no SHOW/HIDE arm is true, default to visible.
 */
export const shouldDaDefaultVisibleWhenHidden = (
  visibilityExpression: Expression | undefined,
  context: DaVisibilityEvaluationContext,
  isDaForm: boolean
): boolean => {
  if (!isDaForm || !visibilityExpression?.enabled) {
    return false;
  }

  if (!visibilityHasShowAndHideConfigured(visibilityExpression)) {
    return false;
  }

  return allVisibilitySubExpressionsFalse(visibilityExpression, context);
};
